import { notifyResize } from "#skyward_window";
import { defineComponent, InjectionKey, onMounted, onUpdated, provide } from "vue";
import { classNames } from "../classes";

export enum Layout
{
    Vertical = "vertical",
    Horizontal = "horizontal"
}

export interface LabeledDataListProps
{
    layout?: Layout;
    isForSelector?: boolean;
    isForLegend?: boolean;
    noSpacing?: boolean;
    alignLabels?: boolean;
    centered?: boolean;
}

export type LabeledDataListOptions = Pick<LabeledDataListProps, "isForSelector" | "isForLegend" | "alignLabels">;

export const labeledDataListOptionsKey: InjectionKey<LabeledDataListOptions> = Symbol("labeledDataListOptions");

// When using this component in Vue, pass <LabeledData> components as children, each with a <LabelPart> and a <DataPart>.
// Labeled data from server-side rendering is converted into the same children in index.tsx.
const LabeledDataList = defineComponent<LabeledDataListProps>({
    name: "sky-labeleddatalist",
    props:
    {
        layout:
        {
            type: String as () => Layout,
            default: Layout.Vertical,
            validator: (value: string) => Object.values(Layout).includes(value as unknown as Layout)
        },
        isForSelector: { type: Boolean, default: false },
        isForLegend: { type: Boolean, default: false },
        noSpacing: { type: Boolean, default: false },
        alignLabels: { type: Boolean, default: false },
        centered: { type: Boolean, default: false }
    },
    setup(props, { slots })
    {
        provide(labeledDataListOptionsKey, props);

        onMounted(() =>
        {
            notifyResize();
        });

        onUpdated(() =>
        {
            notifyResize();
        });

        return () => (
            <dl
                class={{
                    [classNames.labeledDataList]: true,
                    [classNames.horizontal]: props.layout === Layout.Horizontal,
                    [classNames.vertical]: props.layout === Layout.Vertical,
                    [classNames.noSpacing]: props.noSpacing,
                    [classNames.centered]: props.centered
                }}
            >
                {slots.default?.()}
            </dl>
        );
    }
});

export default LabeledDataList;
